import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../config/api';
import toast from 'react-hot-toast';
import DashboardCreationModal from './DashboardCreationModal';
import ShareDashboardModal from './ShareDashboardModal';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  // EllipsisVerticalIcon, // Unused - removed
  EyeIcon,
  PencilIcon,
  ShareIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  HeartIcon,
  ClockIcon,
  ChartBarIcon,
  StarIcon
  // ExclamationTriangleIcon // Unused - removed
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';

const ManagerContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
  padding: 2rem;
`;

const ManagerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.h1};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.lg};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchBar = styled.div`
  position: relative;
  min-width: 300px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.paper};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.md};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 3px ${theme.colors.primary.main}20;
  }

  &::placeholder {
    color: ${theme.colors.text.secondary};
  }
`;

const SearchIcon = styled(MagnifyingGlassIcon)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.25rem;
  height: 1.25rem;
  color: ${theme.colors.text.secondary};
`;

const FilterControls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.paper};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.md};
  font-weight: ${theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border: none;

  ${props => props.variant === 'primary' ? `
    background: ${theme.colors.primary.main};
    color: white;
    
    &:hover {
      background: ${theme.colors.primary.dark};
      transform: translateY(-1px);
    }
  ` : props.variant === 'secondary' ? `
    background: ${theme.colors.background.paper};
    color: ${theme.colors.text.primary};
    border: 1px solid ${theme.colors.border.secondary};
    
    &:hover {
      background: ${theme.colors.background.secondary};
    }
  ` : `
    background: transparent;
    color: ${theme.colors.text.secondary};
    
    &:hover {
      color: ${theme.colors.text.primary};
      background: ${theme.colors.background.secondary};
    }
  `}
`;

const SharedBadge = styled.span`
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.medium};
  background: ${(props) => (props.$mine ? '#dbeafe' : '#fef3c7')};
  color: ${(props) => (props.$mine ? '#1d4ed8' : '#b45309')};
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const DashboardCard = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.xl};
    border-color: ${theme.colors.primary.main};
  }
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${theme.colors.border.secondary};
  position: relative;
`;

const CardTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const DashboardIcon = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: white;
`;

const CardSubtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  margin: 0;
`;

const StatusBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.medium};
  
  ${props => {
    switch (props.status) {
      case 'online':
        return `
          background: ${theme.colors.success.light};
          color: ${theme.colors.success.main};
        `;
      case 'warning':
        return `
          background: ${theme.colors.warning.light};
          color: ${theme.colors.warning.main};
        `;
      case 'offline':
        return `
          background: ${theme.colors.error.light};
          color: ${theme.colors.error.main};
        `;
      default:
        return `
          background: ${theme.colors.background.secondary};
          color: ${theme.colors.text.secondary};
        `;
    }
  }}
`;

const StatusDot = styled.div`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: currentColor;
  animation: ${props => props.status === 'online' ? 'pulse 2s infinite' : 'none'};
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const CardBody = styled.div`
  padding: 1.5rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.text.primary};
`;

const StatLabel = styled.div`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.secondary};
  margin-top: 0.25rem;
`;

const CardPreview = styled.div`
  height: 60px;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  margin-bottom: 1rem;
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
  align-items: center;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border: none;

  ${props => props.variant === 'primary' ? `
    background: ${theme.colors.primary.main};
    color: white;
    
    &:hover {
      background: ${theme.colors.primary.dark};
    }
  ` : props.variant === 'danger' ? `
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.error.main};
    border: 1px solid ${theme.colors.error.main};
    
    &:hover {
      background: ${theme.colors.error.main};
      color: white;
    }
  ` : `
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.text.primary};
    
    &:hover {
      background: ${theme.colors.border.secondary};
    }
  `}
`;

const FavoriteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: ${theme.borderRadius.md};
  color: ${props => props.$isFavorite ? theme.colors.warning.main : theme.colors.text.secondary};
  transition: all 0.2s ease-in-out;

  &:hover {
    background: ${theme.colors.background.secondary};
    transform: scale(1.1);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${theme.colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.h3};
  margin-bottom: 1rem;
`;

const EmptyDescription = styled.p`
  font-size: ${theme.fontSizes.lg};
  margin-bottom: 2rem;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const ManageDashboards = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState([]);
  const [filteredDashboards, setFilteredDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all'); // all, mine, shared, favorites
  const [sortBy, setSortBy] = useState('updated'); // name, updated, created, vessel
  const [favorites, setFavorites] = useState(new Set());
  const [showCreationModal, setShowCreationModal] = useState(false);
  const [shareDashboard, setShareDashboard] = useState(null);

  useEffect(() => {
    loadDashboards();
    loadFavorites();
  }, []);

  useEffect(() => {
    filterAndSortDashboards();
  }, [dashboards, searchTerm, filterBy, sortBy, favorites]);

  const loadDashboards = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/dashboards');
      
      // Handle both array response and wrapped response
      const dashboardsData = Array.isArray(response.data) ? response.data : (response.data?.dashboards || []);
      
      console.log('Loaded dashboards:', dashboardsData.length, dashboardsData);
      setDashboards(dashboardsData);
      
      if (dashboardsData.length === 0) {
        console.log('No dashboards found for user');
      }
    } catch (error) {
      console.error('Error loading dashboards:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Failed to load dashboards: ' + (error.response?.data?.error || error.message));
      setDashboards([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const response = await api.get('/api/dashboards/favorites');
      setFavorites(new Set(response.data.map(f => f.dashboard_id)));
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const filterAndSortDashboards = () => {
    let filtered = [...dashboards];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(dashboard =>
        dashboard.dashboard_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dashboard.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dashboard.vessel_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply category filter
    switch (filterBy) {
      case 'mine':
        filtered = filtered.filter(d => d.user_id === user.id);
        break;
      case 'shared':
        filtered = filtered.filter((d) => d.is_shared && !d.is_owner);
        break;
      case 'favorites':
        filtered = filtered.filter(d => favorites.has(d.id));
        break;
      default:
        break;
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.dashboard_name.localeCompare(b.dashboard_name);
        case 'created':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'vessel':
          return (a.vessel_name || '').localeCompare(b.vessel_name || '');
        case 'updated':
        default:
          return new Date(b.updated_at) - new Date(a.updated_at);
      }
    });

    setFilteredDashboards(filtered);
  };

  const getDashboardIcon = (type) => {
    const icons = {
      system_overview: '📊',
      electrical: '⚡',
      navigation: '🧭',
      engine: '🔧',
      tanks: '⛽',
      weather: '🌤️',
      analytics: '📈',
      fleet: '🚤',
      custom: '🎨'
    };
    return icons[type] || '📊';
  };

  const getDashboardStatus = (dashboard) => {
    const ts = dashboard.last_update || dashboard.last_signalk_at;
    if (!ts) return 'offline';

    const lastUpdate = new Date(ts);
    if (Number.isNaN(lastUpdate.getTime())) return 'offline';

    const hoursAgo = (Date.now() - lastUpdate.getTime()) / (1000 * 60 * 60);
    if (hoursAgo < 1) return 'online';
    if (hoursAgo < 24) return 'warning';
    return 'offline';
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'online': return 'Live';
      case 'warning': return 'Inactive';
      case 'offline': return 'Offline';
      default: return 'Unknown';
    }
  };

  const formatLastUpdate = (timestamp) => {
    if (!timestamp) return 'Never';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  const handleOpenShare = (dashboard) => {
    setShareDashboard(dashboard);
  };

  const handleCloseShare = () => {
    setShareDashboard(null);
  };

  const isOwner = (dashboard) => dashboard.is_owner !== false && dashboard.user_id === user?.id;

  const canEditDashboard = (dashboard) =>
    isOwner(dashboard) || ['owner', 'admin', 'editor'].includes(dashboard.permission_level);

  const handleCreateDashboard = () => {
    setShowCreationModal(true);
  };

  // AI dashboard creation temporarily disabled
  const handleCreateAIDashboard = async () => {
    toast.error('AI dashboard creation is temporarily disabled.');
  };

  const handleCreateManualDashboard = () => {
    navigate('/dashboard/create');
  };

  const handleViewDashboard = (dashboardId) => {
    navigate(`/dashboard/${dashboardId}`);
  };

  const handleEditDashboard = (dashboardId) => {
    navigate(`/dashboard/${dashboardId}/edit?t=${Date.now()}`);
  };

  const handleToggleFavorite = async (dashboardId) => {
    try {
      const isFavorite = favorites.has(dashboardId);
      
      if (isFavorite) {
        await api.delete(`/api/dashboards/${dashboardId}/favorite`);
        setFavorites(prev => {
          const newFavorites = new Set(prev);
          newFavorites.delete(dashboardId);
          return newFavorites;
        });
        toast.success('Removed from favorites');
      } else {
        await api.post(`/api/dashboards/${dashboardId}/favorite`);
        setFavorites(prev => new Set([...prev, dashboardId]));
        toast.success('Added to favorites');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorite');
    }
  };

  const handleSetDefault = async (dashboardId) => {
    try {
      await api.post(`/api/dashboards/${dashboardId}/set-default`);
      
      setDashboards(prevDashboards => 
        prevDashboards.map(dashboard => ({
          ...dashboard,
          is_default: dashboard.id === dashboardId
        }))
      );
      
      toast.success('Default dashboard set successfully');
    } catch (error) {
      console.error('Error setting default dashboard:', error);
      toast.error('Failed to set default dashboard');
    }
  };

  const handleDuplicateDashboard = async (dashboardId) => {
    try {
      await api.post(`/api/dashboards/${dashboardId}/duplicate`);
      toast.success('Dashboard duplicated successfully');
      loadDashboards();
    } catch (error) {
      console.error('Error duplicating dashboard:', error);
      toast.error('Failed to duplicate dashboard');
    }
  };

  const handleDeleteDashboard = async (dashboardId) => {
    toast((t) => (
      <div className="flex flex-col space-y-2">
        <span className="font-medium text-red-600">Delete Dashboard</span>
        <span className="text-sm text-gray-600">This action cannot be undone.</span>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              toast.dismiss(t.id);
              performDeleteDashboard(dashboardId);
            }}
            className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
          >
            Delete
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: 10000 });
    return;
  };

  const performDeleteDashboard = async (dashboardId) => {

    try {
      await api.delete(`/api/dashboards/${dashboardId}`);
      toast.success('Dashboard deleted successfully');
      loadDashboards();
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      toast.error('Failed to delete dashboard');
    }
  };

  if (loading) {
    return (
      <ManagerContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div>Loading dashboards...</div>
        </div>
      </ManagerContainer>
    );
  }

  return (
    <ManagerContainer>
      <ManagerHeader>
        <HeaderLeft>
          <Title>
            <ChartBarIcon className="w-8 h-8" />
            My Dashboards
          </Title>
          <Subtitle>
            Manage and monitor your vessel dashboards
          </Subtitle>
        </HeaderLeft>
        <HeaderActions>
          <SearchBar>
            <SearchIcon />
            <SearchInput
              type="text"
              placeholder="Search dashboards..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBar>
          <Button variant="primary" onClick={handleCreateDashboard}>
            <PlusIcon className="w-5 h-5" />
            New Dashboard
          </Button>
        </HeaderActions>
      </ManagerHeader>

      <FilterControls>
        <Select value={filterBy} onChange={(e) => setFilterBy(e.target.value)}>
          <option value="all">All Dashboards</option>
          <option value="mine">My Dashboards</option>
          <option value="shared">Shared with Me</option>
          <option value="favorites">Favorites</option>
        </Select>
        <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="updated">Last Updated</option>
          <option value="name">Name</option>
          <option value="created">Created Date</option>
          <option value="vessel">Vessel</option>
        </Select>
        <Button variant="ghost">
          <FunnelIcon className="w-4 h-4" />
          More Filters
        </Button>
      </FilterControls>

      {filteredDashboards.length === 0 ? (
        <EmptyState>
          <EmptyIcon>📊</EmptyIcon>
          <EmptyTitle>
            {searchTerm || filterBy !== 'all' ? 'No dashboards found' : 'No dashboards yet'}
          </EmptyTitle>
          <EmptyDescription>
            {searchTerm || filterBy !== 'all' 
              ? 'Try adjusting your search or filters to find what you\'re looking for.'
              : 'Create your first dashboard to start monitoring your vessel systems.'
            }
          </EmptyDescription>
          {!searchTerm && filterBy === 'all' && (
            <Button variant="primary" onClick={handleCreateDashboard}>
              <PlusIcon className="w-5 h-5" />
              Create Your First Dashboard
            </Button>
          )}
        </EmptyState>
      ) : (
        <DashboardGrid>
          {filteredDashboards.map((dashboard) => {
            const status = getDashboardStatus(dashboard);
            const isFavorite = favorites.has(dashboard.id);
            
            return (
              <DashboardCard key={dashboard.id}>
                <CardHeader>
                  <CardTitle>
                    <DashboardIcon>
                      {getDashboardIcon(dashboard.dashboard_type)}
                    </DashboardIcon>
                    <div>
                      <div>
                        {dashboard.dashboard_name}
                        {dashboard.is_shared && !isOwner(dashboard) && (
                          <SharedBadge>Shared with me</SharedBadge>
                        )}
                      </div>
                      <CardSubtitle>{dashboard.vessel_name || 'No vessel assigned'}</CardSubtitle>
                    </div>
                  </CardTitle>
                  <StatusBadge status={status}>
                    <StatusDot status={status} />
                    {getStatusText(status)}
                  </StatusBadge>
                </CardHeader>

                <CardBody>
                  <StatsGrid>
                    <StatItem>
                      <StatValue>{dashboard.card_count || 0}</StatValue>
                      <StatLabel>Cards</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>{dashboard.data_point_count || 0}</StatValue>
                      <StatLabel>Data Points</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>{dashboard.active_alerts || 0}</StatValue>
                      <StatLabel>Alerts</StatLabel>
                    </StatItem>
                  </StatsGrid>

                  <CardPreview>
                    Dashboard Preview
                  </CardPreview>

                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    fontSize: theme.fontSizes.xs,
                    color: theme.colors.text.secondary,
                    marginBottom: '1rem'
                  }}>
                    <span title="Last live boat data received">
                      <ClockIcon className="w-4 h-4 inline mr-1" />
                      {formatLastUpdate(dashboard.last_update || dashboard.last_signalk_at)}
                    </span>
                    <FavoriteButton
                      $isFavorite={isFavorite}
                      onClick={() => handleToggleFavorite(dashboard.id)}
                    >
                      {isFavorite ? (
                        <HeartSolidIcon className="w-5 h-5" />
                      ) : (
                        <HeartIcon className="w-5 h-5" />
                      )}
                    </FavoriteButton>
                  </div>

                  <CardActions>
                    <ActionButton onClick={() => handleViewDashboard(dashboard.id)}>
                      <EyeIcon className="w-4 h-4" />
                      View
                    </ActionButton>
                    {canEditDashboard(dashboard) && (
                      <ActionButton onClick={() => handleEditDashboard(dashboard.id)}>
                        <PencilIcon className="w-4 h-4" />
                        Edit
                      </ActionButton>
                    )}
                    {isOwner(dashboard) && (
                      <ActionButton onClick={() => handleOpenShare(dashboard)} title="Share dashboard">
                        <ShareIcon className="w-4 h-4" />
                        Share
                      </ActionButton>
                    )}
                    {isOwner(dashboard) && (
                      <ActionButton 
                        onClick={() => handleSetDefault(dashboard.id)}
                        title={dashboard.is_default ? "Default Dashboard" : "Set as Default"}
                        style={{ 
                          backgroundColor: dashboard.is_default ? theme.colors.success.main : 'transparent',
                          color: dashboard.is_default ? 'white' : theme.colors.text.secondary
                        }}
                      >
                        <StarIcon className="w-4 h-4" />
                        {dashboard.is_default ? 'Default' : 'Set Default'}
                      </ActionButton>
                    )}
                    {isOwner(dashboard) && (
                      <ActionButton onClick={() => handleDuplicateDashboard(dashboard.id)}>
                        <DocumentDuplicateIcon className="w-4 h-4" />
                      </ActionButton>
                    )}
                    {isOwner(dashboard) && (
                      <ActionButton 
                        variant="danger"
                        onClick={() => handleDeleteDashboard(dashboard.id)}
                        title="Delete Dashboard"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </ActionButton>
                    )}
                  </CardActions>
                </CardBody>
              </DashboardCard>
            );
          })}
        </DashboardGrid>
      )}
      
      <DashboardCreationModal
        isOpen={showCreationModal}
        onClose={() => setShowCreationModal(false)}
        onCreateAI={handleCreateAIDashboard}
        onCreateManual={handleCreateManualDashboard}
      />

      <ShareDashboardModal
        isOpen={!!shareDashboard}
        onClose={handleCloseShare}
        dashboard={shareDashboard}
      />
    </ManagerContainer>
  );
};

export default ManageDashboards;